/**
 * sourcing/render-list.ts — 리스트 렌더 (날짜 섹션 / 클러스터 / 행 / 태그 편집 / 묶기 확인)
 *
 * SourcingView.ts 에서 분리 (M4, 2026-07-08 — deal-detail 분해 계획 v2 패턴 이식).
 * 이동만 — 동작·마크업 변화 없음. 전부 "state → HTML 문자열" 순수 함수.
 * 이벤트는 data-action 배선이라 핸들러는 뷰 메서드 그대로.
 */

import * as api from '../../api/client';
import { escapeHtml, fmtDate } from '../../lib/cells';
import { renderRegistrationChip } from '../../lib/condition-chip';
import { renderRfqBubbleMeta } from '../../lib/rfqBubble';
import type { SourcingData } from './types';

// 회신 수량이 요청보다 적을 때만 버블에 부족 EA 뱃지(마이그195). 요청만큼/이상·미입력은 정상 → 표시 안 함.
function respondedBadge(r: api.SourcingRfqBubble, requestedQty: number): string {
  const rq = (r as { responded_quantity?: number | null }).responded_quantity;
  // 부족(요청보다 적게 회신)일 때만 표시 — 충족/초과·미입력은 정상이라 뱃지 없음(마이그195).
  if (rq == null || requestedQty == null || rq >= requestedQty) return '';
  // 요청 수량도 작게 붙여 부족 정도를 한눈에: 예) 5/30EA
  return `<span class="rfq-bubble-qty rfq-bubble-qty-short" title="회신 ${rq}EA / 요청 ${requestedQty}EA">${rq}<span class="rfq-bubble-qty-req">/${requestedQty}</span>EA</span>`;
}

export function renderBody(data: SourcingData): string {
  if (data.loading && data.needs.length === 0) {
    return `<p class="text-muted" style="padding:32px;text-align:center">불러오는 중…</p>`;
  }
  if (data.needs.length === 0) {
    return `<p class="text-muted" style="padding:32px;text-align:center">니즈가 없습니다.</p>`;
  }
  // 1) 클러스터 빌드 — group_key 별(null=단독). 대표 날짜 = 묶음 내 최신 created_at.
  const byKey = new Map<string, api.SourcingListNeed[]>();
  for (const n of data.needs) {
    const key = n.sourcing_group_key ?? `solo:${n.id}`;
    const arr = byKey.get(key);
    if (arr) arr.push(n);
    else byKey.set(key, [n]);
  }
  // 정렬/날짜 키 = sort_at ?? created_at (ISO 문자열은 사전식=시간순).
  const eff = (x: api.SourcingListNeed) => x.sourcing_sort_at ?? x.created_at;
  type Cluster = { needs: api.SourcingListNeed[]; at: string };
  const clusters: Cluster[] = [...byKey.values()].map((needs) => ({
    needs,
    at: needs.reduce((m, x) => (eff(x) > m ? eff(x) : m), eff(needs[0]!)),
  }));
  clusters.sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0));

  // 2) 날짜 섹션 (클러스터 대표 날짜 기준)
  const dateGroups = new Map<string, Cluster[]>();
  for (const c of clusters) {
    const d = c.at.slice(0, 10);
    const arr = dateGroups.get(d);
    if (arr) arr.push(c);
    else dateGroups.set(d, [c]);
  }

  const header = `
      <div class="sourcing-table-header-row">
        <span></span><span>파트넘버</span><span>디스크립션</span><span>컨디션</span><span>수량</span>
        <span>연결 딜</span><span>영업 요청</span><span>소싱 메모</span><span>매입처-가격</span><span></span>
      </div>`;
  const sections = [...dateGroups.entries()].map(([date, cls]) => `
      <section class="sourcing-date-section">
        <div class="sourcing-date-head"><span class="sourcing-date-title">${escapeHtml(fmtDate(date))}</span></div>
        ${cls.map((c) => renderCluster(c.needs, data.menuId)).join('')}
      </section>`).join('');
  const footer = data.loadingMore
    ? `<p class="sourcing-list-note">불러오는 중…</p>`
    : data.nextCursor
      ? `<p class="sourcing-list-note">스크롤하면 더 불러옵니다…</p>`
      : `<p class="sourcing-list-note">총 ${data.needs.length}건</p>`;
  return `${header}${sections}${footer}${renderMergePopover(data)}${renderTagEditor(data)}`;
}

export function renderTagEditor(data: SourcingData): string {
  const t = data.tagEditor;
  if (!t) return '';
  const need = data.needs.find((n) => n.id === t.needId);
  if (!need) return '';
  const isSales = t.kind === 'sales';
  const opts    = isSales
    ? [...api.SALES_REQUEST_TAGS]
    : api.SOURCING_MEMO_TAGS.filter((x) => x !== '소싱 메모');
  const current = isSales ? need.sales_request_tags : need.sourcing_memo_tags;
  const title   = isSales ? '영업 요청' : '소싱 메모';
  const left = Math.max(12, Math.min(window.innerWidth - 260, t.x));
  const top  = Math.max(12, Math.min(window.innerHeight - 60 - opts.length * 48, t.y));
  return `
      <div class="sourcing-tag-overlay" data-action="tag-close"></div>
      <div class="sourcing-tag-pop" style="left:${left}px;top:${top}px">
        <div class="sourcing-tag-pop-title">${escapeHtml(title)}</div>
        ${opts.map((o) => {
          const on = current.includes(o);
          return `<button class="sourcing-tag-opt ${on ? 'is-on' : ''}" type="button" data-action="tag-toggle" data-tag="${escapeHtml(o)}">
                    <span class="sourcing-tag-check">${on ? '☑' : '☐'}</span> ${escapeHtml(o)}
                  </button>`;
        }).join('')}
      </div>`;
}

export function renderCluster(needs: api.SourcingListNeed[], menuId: string | null): string {
  const grouped = needs.length > 1;
  const repId = needs[0]!.id;
  if (!grouped) {
    // 단독 니즈 — ⚙ 는 그 행에 (모호하지 않음).
    const rows = needs.map((n, i) => renderRow(n, menuId, false, i === 0, repId)).join('');
    return `<div class="sourcing-cluster single">${rows}</div>`;
  }
  // 그룹 — ⚙ 는 행에서 빼고 오른쪽 전용 칸에 세로 가운데(그룹 전체 대상임을 명확히).
  const rows = needs.map((n) => renderRow(n, menuId, true, false, repId)).join('');
  return `
      <div class="sourcing-cluster grouped">
        <div class="sourcing-cluster-head">
          <span class="sourcing-cluster-grip" draggable="true"
                data-action="drag-group" data-group-key="${escapeHtml(needs[0]!.sourcing_group_key ?? '')}"
                title="그룹 전체를 드래그해 다른 곳에 합치기">⠿ 그룹 ${needs.length}건</span>
          <button class="sourcing-cluster-dissolve" type="button"
                  data-action="group-dissolve" data-need-id="${escapeHtml(needs[0]!.id)}">그룹 해제</button>
        </div>
        <div class="sourcing-cluster-body">
          ${rows}
          <div class="sourcing-group-config-anchor" aria-hidden="true">
            <button class="sourcing-supplier-config sourcing-group-config" type="button" data-action="supplier-open"
                    data-need-id="${escapeHtml(repId)}" title="그룹 매입처 지정" aria-label="그룹 매입처 지정">⚙</button>
          </div>
        </div>
      </div>`;
}

export function renderMergePopover(data: SourcingData): string {
  const m = data.mergeConfirm;
  if (!m) return '';
  const left = Math.max(12, Math.min(window.innerWidth - 240, m.x));
  const top  = Math.max(12, Math.min(window.innerHeight - 120, m.y));
  return `
      <div class="sourcing-merge-overlay" data-action="merge-cancel"></div>
      <div class="sourcing-merge-popover" style="left:${left}px;top:${top}px">
        <p class="sourcing-merge-text">그룹으로 묶을까요?</p>
        <div class="sourcing-merge-actions">
          <button class="btn btn-ghost btn-sm" type="button" data-action="merge-cancel">취소</button>
          <button class="btn btn-primary btn-sm" type="button" data-action="merge-apply">묶기</button>
        </div>
      </div>`;
}

export function renderRow(
  n: api.SourcingListNeed,
  menuId: string | null,
  grouped = false,
  canConfigure = false,
  repId = '',
): string {
  const part = n.option_part
    ? `<code>${escapeHtml(n.option_part)}</code>`
    : renderRegistrationChip(n.option_part);
  const deal = n.deal_id
    ? `<a target="_blank" rel="noopener" draggable="false" href="/deal/${escapeHtml(n.deal_id)}" class="sourcing-deal-link">${escapeHtml(n.deal_name ?? '딜')}</a>`
    : `<span class="text-muted">미연결</span>`;
  const tagCell = (kind: 'sales' | 'memo', arr: string[], placeholder: string) =>
    `<button class="sourcing-tagcell" type="button" data-action="tag-open"
             data-need-id="${escapeHtml(n.id)}" data-kind="${kind}">
      ${arr.length
        ? arr.map((t) => `<span class="sourcing-tag sourcing-tag-${kind}">${escapeHtml(t)}</span>`).join(' ')
        : `<span class="sourcing-tagcell-empty">${escapeHtml(placeholder)}</span>`}
     </button>`;
  // 개별 니즈에서 숨긴(is_hidden) 매입처는 버블에서 제외(마이그142). 데이터는 보존됨.
  const visibleRfqs = n.rfqs.filter((r) => !r.is_hidden);
  const bubblesHtml = visibleRfqs.length
    ? visibleRfqs.map((r) => {
        const priced = r.price_amount != null;
        return `<button class="sourcing-result-pill ${priced ? 'is-priced' : ''}${r.purchase_requested ? ' is-purchase-requested' : ''}" type="button" draggable="false"
                  data-action="price-open" data-rfq-id="${escapeHtml(r.id)}"
                  title="${escapeHtml(r.supplier_name)} — 클릭해 가격 입력"><span class="rfq-bubble-supplier">${r.purchase_requested ? '🛒 ' : ''}${escapeHtml(r.supplier_name)}</span>${renderRfqBubbleMeta(r)}${respondedBadge(r, n.quantity)}</button>`;
      }).join(' ')
    : '';
  const configBtn = canConfigure
    ? `<button class="sourcing-supplier-config" type="button" data-action="supplier-open" data-need-id="${escapeHtml(repId)}" title="그룹 매입처 지정" aria-label="그룹 매입처 지정">⚙</button>`
    : '';
  // 개별 니즈 거래처 표시 설정 — 그룹 매입처가 있으면(rfq 존재) 표시(마이그142).
  const visBtn = n.rfqs.length > 0
    ? `<button class="sourcing-visibility-config" type="button" data-action="visibility-open" data-need-id="${escapeHtml(n.id)}" title="거래처 표시 설정" aria-label="거래처 표시 설정">👁</button>`
    : '';
  const bubbles = `${configBtn}${visBtn}${bubblesHtml || (canConfigure ? '' : '<span class="text-muted">-</span>')}`;

  const optStyle = 'width:100%;border:0;background:transparent;text-align:left;font:inherit';
  const menu = menuId === n.id
    ? `<div class="deal-tag-pop-overlay" data-action="need-menu-close"></div>
       <div class="deal-tag-pop sourcing-row-menu">
         ${grouped ? `<button class="deal-tag-opt" type="button" data-action="group-remove" data-need-id="${escapeHtml(n.id)}" style="${optStyle}">그룹에서 빼기</button>` : ''}
         <button class="deal-tag-opt" type="button" data-action="need-purchase-request" data-need-id="${escapeHtml(n.id)}"
                 data-requested="${n.purchase_requested ? '1' : '0'}" style="${optStyle}">${n.purchase_requested ? '구매 요청 취소' : '구매 요청'}</button>
         <button class="deal-tag-opt text-danger" type="button" data-action="need-delete" data-need-id="${escapeHtml(n.id)}"
                 style="${optStyle}">삭제</button>
       </div>`
    : '';

  // 복사 형식(슬랙 규격): "파트넘버 디스크립션 컨디션 수량EA" — 한 칸 구분, 수량+EA 붙임.
  const copyText = [
    n.option_part,
    n.item_text,
    n.requested_condition,
    n.quantity != null ? `${n.quantity}EA` : '',
  ].filter(Boolean).join(' ');

  // 행 "구매요청" 표시 = 니즈 레벨 플래그 OR rfq 중 하나라도 구매요청됨(마이그140).
  const isRequested = n.purchase_requested || n.rfqs.some((r) => r.purchase_requested);
  return `
      <div class="sourcing-row${isRequested ? ' need-row-requested' : ''}" data-action="drag-need" data-need-id="${escapeHtml(n.id)}">
        <span class="sourcing-drag-handle" draggable="true" title="드래그해서 묶기" aria-label="드래그">⠿</span>
        <span class="sourcing-cell-part">${part}${isRequested ? ` <span class="need-purchase-pill">🛒 구매 요청</span>` : ''}</span>
        <span class="sourcing-cell-desc">${escapeHtml(n.item_text ?? '-')}${copyText
          ? `<button class="sourcing-desc-copy" type="button" data-action="copy-desc"
                     data-copy="${escapeHtml(copyText)}"
                     title="복사 (파트넘버 디스크립션 컨디션 수량)" aria-label="복사">⧉</button>`
          : ''}</span>
        <span class="sourcing-cell-cond">${escapeHtml(n.requested_condition ?? '-')}</span>
        <span>${escapeHtml(String(n.quantity))} EA</span>
        <span>${deal}</span>
        <span>${tagCell('sales', n.sales_request_tags, '영업 요청')}</span>
        <span>${tagCell('memo', n.sourcing_memo_tags, '소싱 메모')}</span>
        <span class="sourcing-cell-bubbles">${bubbles}</span>
        <span class="sourcing-cell-action deal-tag-cell">
          <button class="btn btn-ghost btn-icon" type="button" data-action="need-menu-open" data-need-id="${escapeHtml(n.id)}"
                  aria-label="작업">⋯</button>
          ${menu}
        </span>
      </div>`;
}
